import { useState, useCallback, useMemo } from "react";
import Combobox from "react-widgets/Combobox";

import "react-widgets/styles.css";

import { useAutocompleteSuggestions } from "./use-autocomplete-suggestions";
import { ClickableArea, GreenP, PushToRightArea } from "../../../global.styles";
import { SpacerVertical } from "../../UI/spacers/spacers.component";

interface Props {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
  placeholder: string;
  defaultValue?: string;
  title?: string;
}

// This uses the Combobox from "react-widgets" (https://jquense.github.io/react-widgets/docs/Combobox)
const AddressInput = ({ onPlaceSelect, placeholder, defaultValue, title }: Props) => {
  const [inputValue, setInputValue] = useState<string>(defaultValue || "");
  // Track to make sure that a place has been selected, not just typed in
  const [placeSelected, setPlaceSelected] = useState(false);

  // useMemo is used to avoid re-creating the requestOptions object on every render
  // which would cause infinite loop
  const requestOptions: Partial<google.maps.places.AutocompleteRequest> =
    useMemo(
      () => ({
        includedRegionCodes: ["uk"],
        language: "en",
        locationBias: {
          north: 51.5546,
          south: 51.3453,
          east: -2.3609,
          west: -2.8725,
        },
      }),
      []
    );

  const { suggestions, resetSession, isLoading } = useAutocompleteSuggestions(
    inputValue,
    requestOptions
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    onPlaceSelect(null);
    setPlaceSelected(false); // Reset place selection state
  }, [onPlaceSelect]);

  const handleInputChange = useCallback(
    (value: google.maps.places.PlacePrediction | string) => {
      if (typeof value === "string") {
        setInputValue(value);
        setPlaceSelected(false); // user is typing, no valid selection yet
      }
    },
    []
  );

  const handleSelect = useCallback(
    (prediction: google.maps.places.PlacePrediction | string) => {
      if (typeof prediction === "string") return;

      const place = prediction.toPlace();
      place
        .fetchFields({
          fields: ["displayName", "formattedAddress", "location"],
        })
        .then(() => {
          resetSession();
          const displayAddress =
            place.formattedAddress?.split(",")[0] === place.displayName
              ? place.formattedAddress
              : place.displayName + ", " + place.formattedAddress;
          onPlaceSelect(place);
          setInputValue(displayAddress || "");
          setPlaceSelected(true); // mark as valid selection
        });
    },
    [onPlaceSelect, resetSession]
  );

  const predictions = useMemo(
    () =>
      suggestions
        .filter((suggestion) => suggestion.placePrediction)
        .map(({ placePrediction }) => placePrediction!),
    [suggestions]
  );

  const handleBlur = useCallback(() => {
    // If the user has not selected a place, reset the input value
    // This is to ensure that if the user types something and then clicks away,
    // the input does not retain the typed value if it was not a valid selection.
    if (!placeSelected) {
      setInputValue("");
      onPlaceSelect(null);
    }
  }, [placeSelected, onPlaceSelect]);

  return (
    <div className="autocomplete-container">
      <p><strong>{title}</strong></p>
      <Combobox
        placeholder={placeholder}
        data={predictions}
        dataKey="placeId"
        textField="text"
        value={inputValue}
        onChange={handleInputChange}
        onSelect={handleSelect}
        busy={isLoading}
        // Since the Autocomplete Service API already returns filtered results
        // always want to display them all.
        filter={() => true}
        focusFirstItem={true}
        hideEmptyPopup
        hideCaret
        onBlur={handleBlur} // Ensure to handle blur to reset the session (user doesn't select a place)
      />
      {inputValue && (
        <SpacerVertical $paddingRem={0.5}>
          <PushToRightArea>
            <ClickableArea onClick={handleClear}>
              <GreenP>CLEAR</GreenP>
            </ClickableArea>
          </PushToRightArea>
        </SpacerVertical>
      )}
    </div>
  );
};

export default AddressInput;
